import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { UtilityService } from '../../shared/services/utility.service';

/**
 * Fun Vibers — single-product sales/landing page for the
 * Metabology™ Photonic Activator flash sale.
 *
 * Attribution: the referring rep's WebAlias is the first segment of the URL
 * (e.g. /pokey/funvibers). It is captured app-wide as `enrollerInfo` at load
 * and is automatically attached to any order placed in the session. The
 * "Buy Now" CTAs navigate to the existing Fun Vibes discount store
 * (/{webAlias}/funstore) so the alias stays in the URL and the credit follows
 * the visitor all the way to checkout — exactly mirroring the weightloss page.
 */
@Component({
  selector: 'app-funvibers',
  templateUrl: './funvibers.component.html',
  styleUrls: ['./funvibers.component.scss']
})
export class FunvibersComponent implements OnInit, AfterViewInit, OnDestroy {
  /** Long hero film (landscape 16:9). */
  heroVideoSrc = 'assets/shareactivatordiscount/photonicact.mp4';
  heroPoster = 'assets/shareactivatordiscount/poster-hero.png';

  /** 26s "see it in action" feature film (landscape 16:9). */
  featureVideoSrc = 'assets/shareactivatordiscount/activator2.mp4';
  featurePoster = 'assets/shareactivatordiscount/poster-feature.png';

  /** Customer testimonial films (portrait / phone) with their poster frames. */
  testimonialVideos = [
    {
      src: 'assets/shareactivatordiscount/testimonial1.mp4',
      poster: 'assets/shareactivatordiscount/poster-testimonial1.png'
    },
    {
      src: 'assets/shareactivatordiscount/testimonial2.mp4',
      poster: 'assets/shareactivatordiscount/poster-testimonial2.png'
    }
  ];

  /** Product imagery. */
  productImage = 'assets/new/activator.png';

  benefits = [
    {
      title: 'Sustained Energy',
      text: 'Promotes sustained energy and metabolic balance throughout your day.'
    },
    {
      title: 'Cellular Renewal',
      text: 'Supports cellular renewal and mitochondrial efficiency.'
    },
    {
      title: 'Strength & Resilience',
      text: 'Helps maintain strength, flexibility, and physical resilience.'
    },
    {
      title: 'Antioxidant Defense',
      text: 'Provides antioxidant support to help combat oxidative stress.'
    },
    {
      title: 'Immune Support',
      text: 'Encourages immune system function for everyday wellness.'
    },
    {
      title: '84 Essential Minerals',
      text: "From America's oldest inland sea — bio-intelligent and photonic."
    }
  ];

  private observer?: IntersectionObserver;

  constructor(
    private router: Router,
    private utilityService: UtilityService,
    private host: ElementRef<HTMLElement>
  ) {}

  ngOnInit(): void {
    window.scrollTo(0, 0);
    this.utilityService.showSimplifiedheader(true);
  }

  ngAfterViewInit(): void {
    // Progressive, staggered reveal of sections as they scroll into view.
    const els = Array.from(
      this.host.nativeElement.querySelectorAll<HTMLElement>('[data-reveal]')
    );

    if (!('IntersectionObserver' in window)) {
      els.forEach(el => el.classList.add('in-view'));
      return;
    }

    this.observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
            this.observer?.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -8% 0px' }
    );

    els.forEach(el => this.observer!.observe(el));
  }

  /**
   * Destination for every "Buy Now" CTA — the existing Fun Vibes discount
   * store. Routes are matched *after* APP_BASE_HREF, which is set to the rep's
   * /{webAlias} at bootstrap (see baseUrl.ts). So we navigate to the bare
   * route: the base href keeps the alias in the browser URL (e.g.
   * /pokey/funstore) and the referral attribution follows the visitor through
   * to checkout automatically. (Prefixing the alias here would target a
   * non-existent "/pokey/funstore" route and bounce to home.)
   */
  get storeLink(): string {
    return '/funstore';
  }

  /** Programmatic navigation fallback for buttons that aren't plain links. */
  goToStore(): void {
    this.router.navigateByUrl(this.storeLink);
  }

  ngOnDestroy(): void {
    this.utilityService.showSimplifiedheader(false);
    this.observer?.disconnect();
  }
}
